import { container } from '../../../context/module';
import { FileUpload } from '../../../models/file-upload';
import { FileDownloadService } from '../../../services/file-download.service';
import { MetricsService } from '../../../services/metrics.service';
import { MetricEventName } from '../../../types/metric-events';

/**
 * FIX-8 (UX-SPEC.md section 8.4): "Download that result" on the tool page, and "Download result"
 * in a Re-edit modal opened from the tool page's Re-run list, where no result card is mounted.
 * (A modal opened from a card on /downloads is handed that card's own Download handler instead.)
 *
 * It takes the card's Download path for a one-file result: the SAME `FILE_DOWNLOAD` event the card
 * emits (`file-list-item.component.tsx` emitFileDownload: workflowId, page, source, zip) and the
 * SAME `FileDownloadService.downloadFile` call on the same output name, inside the user's click --
 * so the edge records the download (`/downloads/<file>`, never the preview URL) exactly as the card
 * would. Only a Quick convert card ever matches an unchanged run here, so `source` is `convert`.
 *
 * A many-file result has no one URL that "is the file" (the card's Download opens its file list),
 * so this answers false and the caller takes the user to /downloads, where that card's own keys do
 * it. Nothing is counted then: the click on the card is the download.
 *
 * Many vs one is decided the way the card decides it -- `result.output` an array, or more than one
 * `outputFiles` -- and NOT from `result.zip`: every result carries a ZIP name (the card's optional
 * ZIP key; `ziped` says whether that ZIP was built), a one-file result included, so a `zip` check
 * sent every "Download that result" to /downloads (UX2-02).
 */
export function downloadExistingResult(card: FileUpload): boolean {
    if (!canDownloadExistingResult(card)) return false;
    const output = card.result.output as unknown as string;
    const metrics = container.get(MetricsService);
    metrics.downloadedThisSession = true;
    metrics.emit(MetricEventName.FILE_DOWNLOAD, {
        workflowId: card.workflowId ?? '',
        page: typeof location !== 'undefined' ? location.pathname : '',
        source: 'convert',
        zip: false,
    });
    const tokens = output.split('\\');
    container.get(FileDownloadService).downloadFile(card, tokens[tokens.length - 1]);
    return true;
}

/**
 * Whether {@link downloadExistingResult} would download `card` (a one-file result with its output name), without
 * doing it. The Re-edit modal asks this of a card that is NOT the one it was opened from (fu-t10 F1): only then is
 * "Download result" honest for that card.
 */
export function canDownloadExistingResult(card: FileUpload | null | undefined): boolean {
    const output = card?.result?.output as unknown;
    if (!card || card.result?.error === true || typeof output !== 'string' || !output) return false;
    const outputFiles = (card.result as { outputFiles?: unknown } | undefined)?.outputFiles;
    return !(Array.isArray(outputFiles) && outputFiles.length > 1);
}
